/** @velyn-custom BEGIN — vely-desk-cards.js
 *  Mobile carousel for Vely Desk Cards — reuses window.Swiper + VelySwiperPagination pill.
 *  Desktop: flex grid (multi-content parity). Mobile stack or slider per section setting.
 */
(function () {
  const MOBILE_MAX = 767.98;

  function debounce(fn, wait) {
    let timer;
    return function debounced(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  class VelyDeskCards extends HTMLElement {
    connectedCallback() {
      if (this._velyDcInitStarted) return;
      this._velyDcInitStarted = true;

      this.enableCarousel = this.dataset.enableCarouselMobile === "true";
      this.itemsPerRow = parseFloat(this.dataset.itemsPerRow) || 1;
      this.spacing = parseFloat(this.dataset.spacing) || 16;
      this.freeScroll = this.dataset.freeScroll === "true";
      this.track = this.querySelector(".vely-desk-cards__track");
      this.paginationEl = this.querySelector(".vely-swiper-pagination-pill");
      this._savedWidth = window.innerWidth;
      this._originalHtml = this.track?.innerHTML || "";

      this._onSectionLoad = (event) => {
        if (event.detail?.sectionId !== this.dataset.sectionId) return;
        this._destroySwiper(false);
        this._originalHtml = this.track?.innerHTML || "";
        this.waitForSwiper(() => this._syncLayout());
      };
      document.addEventListener("shopify:section:load", this._onSectionLoad);

      this._debouncedResize = debounce(() => this._onResize(), 250);
      window.addEventListener("resize", this._debouncedResize, { passive: true });

      this.waitForSwiper(() => this._syncLayout());
    }

    disconnectedCallback() {
      document.removeEventListener("shopify:section:load", this._onSectionLoad);
      window.removeEventListener("resize", this._debouncedResize);
      this._destroySwiper(true);
      this._velyDcInitStarted = false;
      this.classList.remove("vely-desk-cards--ready");
    }

    waitForSwiper(callback, attempt = 0) {
      const hasSwiper = typeof window.Swiper === "function";
      const needsPagination = this.enableCarousel && this.paginationEl;
      const hasPaginationApi = !needsPagination || Boolean(window.VelySwiperPagination?.refresh);

      if (hasSwiper && hasPaginationApi) {
        callback();
        return;
      }
      if (attempt > 160) {
        console.warn("[vely-desk-cards] Swiper or VelySwiperPagination not available");
        return;
      }
      setTimeout(() => this.waitForSwiper(callback, attempt + 1), 50);
    }

    _isMobile() {
      return window.innerWidth <= MOBILE_MAX;
    }

    _onResize() {
      const width = window.innerWidth;
      const crossed =
        (width <= MOBILE_MAX && this._savedWidth > MOBILE_MAX) ||
        (width > MOBILE_MAX && this._savedWidth <= MOBILE_MAX);
      this._savedWidth = width;
      if (crossed || (this._isMobile() && this.enableCarousel)) {
        this._syncLayout();
      }
    }

    _syncLayout() {
      if (!this.track) return;

      if (this._isMobile() && this.enableCarousel) {
        this._initSwiper();
      } else {
        this._destroySwiper(true);
      }
    }

    refreshPagination() {
      if (!this._swiper || !this.paginationEl) return;
      window.VelySwiperPagination?.refresh?.(this._swiper, this.paginationEl);
      this.syncControls(this._swiper);
    }

    syncControls(swiper) {
      const controls = this.querySelector("[data-vely-pagination-anchor]");
      if (!controls || !swiper) return;

      swiper.update?.();
      const minT =
        typeof swiper.minTranslate === "function" ? swiper.minTranslate() : 0;
      const maxT =
        typeof swiper.maxTranslate === "function" ? swiper.maxTranslate() : 0;
      const travel = Math.abs(maxT - minT);
      const snaps = swiper.snapGrid?.length ?? 0;
      const canMove = Boolean(swiper.allowSlideNext || swiper.allowSlidePrev);
      const canSlide = !swiper.isLocked && canMove && snaps > 1 && travel > 1;

      controls.hidden = !canSlide;
      controls.setAttribute("aria-hidden", canSlide ? "false" : "true");
    }

    _buildSlideHtml() {
      const temp = document.createElement("div");
      temp.innerHTML = this._originalHtml;
      temp.querySelectorAll(".switch-slide__mobile, .vely-desk-cards__item").forEach((el) => {
        el.classList.remove("switch-slide__mobile");
        el.classList.add("swiper-slide");
      });
      return temp.innerHTML;
    }

    _initSwiper() {
      const SwiperCtor = window.Swiper;
      if (typeof SwiperCtor !== "function" || !this.track || !this.enableCarousel) return;
      if (!this._isMobile()) return;

      if (this._swiper) {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }

      const slideHtml = this._buildSlideHtml();
      this.track.classList.add("swiper");
      this.track.innerHTML = `<div class="swiper-wrapper">${slideHtml}</div>`;

      const slidesPerView = this.freeScroll ? "auto" : this.itemsPerRow;
      const freeMode = this.freeScroll;

      try {
        this._swiper = new SwiperCtor(this.track, {
          slidesPerView,
          spaceBetween: this.spacing,
          freeMode,
          grabCursor: true,
          watchSlidesProgress: true,
          watchOverflow: true,
          observer: true,
          observeParents: true,
          on: {
            init: (swiper) => {
              this.refreshPagination();
              this.syncControls(swiper);
            },
            slideChange: (swiper) => {
              if (!this.paginationEl) return;
              window.VelySwiperPagination?.setActiveBullet?.(
                swiper,
                this.paginationEl
              );
            },
            resize: (swiper) => {
              this.refreshPagination();
              this.syncControls(swiper);
            },
          },
        });
      } catch (error) {
        this._swiper = null;
        console.warn("[vely-desk-cards] Swiper init failed", error);
        return;
      }

      this.refreshPagination();
      requestAnimationFrame(() => {
        this.refreshPagination();
        this.syncControls(this._swiper);
      });
      this.classList.add("vely-desk-cards--ready");
    }

    _destroySwiper(restoreDom) {
      if (this._swiper) {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }

      if (!restoreDom || !this.track) return;

      this.track.classList.remove(
        "swiper",
        "swiper-initialized",
        "swiper-horizontal",
        "swiper-backface-hidden"
      );
      this.track.innerHTML = this._originalHtml;
      this.classList.remove("vely-desk-cards--ready");
    }
  }

  if (!customElements.get("vely-desk-cards")) {
    customElements.define("vely-desk-cards", VelyDeskCards);
  }
})();
/** @velyn-custom END — vely-desk-cards.js */
